<script setup lang="ts">
import { onMounted } from 'vue';
import { RouterView, RouterLink, useRouter } from 'vue-router';
import { useAuthStore } from '@core/auth/auth.store';
import { useStammdatenStore } from '@stores/stammdaten.store';
import { useOnline } from '@core/composables/useOnline';
import { useAutoLogout } from '@core/composables/useAutoLogout';

const auth = useAuthStore();
const stammdaten = useStammdatenStore();
const online = useOnline();
const router = useRouter();

function abmelden() {
  // Erst zur Login-Seite navigieren, dann Tokens löschen: so verlässt die aktuelle
  // View den Screen, bevor sie eine Abfrage ohne Token schickt („Not authenticated").
  auth.logout();
  router.replace({ name: 'login' });
}

// Automatische Abmeldung: 15 Min Inaktivität ODER 60 Min harte Obergrenze.
useAutoLogout(
  () => auth.isLoggedIn,
  () => {
    auth.logout();
    router.replace({ name: 'login', query: { abgelaufen: '1' } });
  },
);

onMounted(() => {
  // Stammdaten einmalig laden (Einheiten/Kategorien fuer alle Views).
  stammdaten.laden().catch(() => { /* Fehler werden in den Views sichtbar */ });
});
</script>

<template>
  <div class="flex h-full flex-col">
    <!-- Kopfzeile -->
    <header class="flex items-center justify-between border-b border-border bg-surface px-4 py-3">
      <div class="flex items-center gap-2">
        <span class="inline-flex h-7 w-7 items-center justify-center rounded-sm bg-akzent text-sm font-semibold text-white">B</span>
        <span class="text-lg font-semibold">BuildBook</span>
      </div>
      <div class="flex items-center gap-3">
        <!-- Verbindungs-Status: verbunden = live mit der einen Datenbank -->
        <span
          class="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs"
          :class="online ? 'bg-ampel-gruen-bg text-ampel-gruen' : 'bg-hover text-text-muted'"
          :title="online ? 'Live mit dem Server verbunden' : 'Keine Verbindung zum Server'"
        >
          <span class="inline-block h-2 w-2 rounded-full" :class="online ? 'bg-ampel-gruen' : 'bg-text-muted'"></span>
          <span class="hidden sm:inline">{{ online ? 'Verbunden' : 'Offline' }}</span>
        </span>
        <span class="hidden text-sm text-text-muted sm:inline">{{ auth.user?.name }}</span>
        <button
          class="rounded-sm px-2 py-1 text-sm text-akzent hover:bg-akzent-10"
          @click="abmelden"
        >
          Abmelden
        </button>
      </div>
    </header>

    <!-- Offline-Hinweis: server-autoritativ → ohne Verbindung kein Speichern -->
    <div v-if="!online" class="border-b border-border bg-ampel-orange-bg px-4 py-2 text-center text-sm text-ampel-orange">
      Keine Verbindung zum Server — Ansehen und Speichern sind gerade nicht möglich.
    </div>

    <!-- Inhalt -->
    <main class="flex-1 overflow-y-auto">
      <RouterView />
    </main>

    <!-- Untere Navigation (mobil-first) -->
    <nav class="grid grid-cols-3 border-t border-border bg-surface" style="padding-bottom: env(safe-area-inset-bottom)">
      <RouterLink
        to="/"
        class="flex flex-col items-center gap-0.5 py-2 text-xs text-text-muted"
        active-class="text-akzent"
      >
        <span class="text-lg">≡</span>
        Buchungen
      </RouterLink>
      <RouterLink
        to="/erfassen"
        class="flex flex-col items-center gap-0.5 py-2 text-xs text-text-muted"
        active-class="text-akzent"
      >
        <span class="text-lg">＋</span>
        Erfassen
      </RouterLink>
      <RouterLink
        to="/einstellungen"
        class="flex flex-col items-center gap-0.5 py-2 text-xs text-text-muted"
        active-class="text-akzent"
      >
        <span class="text-lg">⚙</span>
        Einstellungen
      </RouterLink>
    </nav>
  </div>
</template>
